import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CreditCard,
  Receipt,
  Sparkles,
  ArrowUpRight,
  CalendarDays,
  ArrowRight,
  Loader2,
  FileText,
  Download,
  ExternalLink,
  Mail,
} from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { createPortalSession, getInvoices, resendInvoiceEmail } from "@/utils/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/billing")({
  component: BillingPage,
  head: () => ({ meta: [{ title: "Billing · Serenity" }] }),
});

function BillingPage() {
  const { data: user } = useQuery({
    queryKey: ["auth-user"],
    queryFn: async () => (await supabase.auth.getUser()).data.user,
  });

  const { subscription, isActive, isLoading: subLoading } = useSubscription(user?.id);

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ["payment-history", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("id, payment_status, amount_paid_cents, scheduled_at, created_at, therapists(name, specialty)")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []).map((b) => ({
        id: b.id,
        description: b.therapists?.name ? `Therapy session — ${b.therapists.name}` : "Therapy session",
        status: b.payment_status,
        amount_cents: b.amount_paid_cents,
        scheduled_at: b.scheduled_at,
        created_at: b.created_at,
      }));
    },
  });

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ["stripe-invoices", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const res = await getInvoices({ data: { environment: getStripeEnvironment() } });
      if ("error" in res) throw new Error(res.error);
      return res.invoices;
    },
  });

  const [portalLoading, setPortalLoading] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [confirmInvoice, setConfirmInvoice] = useState<{ id: string; number: string | null } | null>(null);

  const handleResend = async (invoiceId: string) => {
    try {
      setResendingId(invoiceId);
      const result = await resendInvoiceEmail({
        data: { invoiceId, environment: getStripeEnvironment() },
      });
      if ("error" in result) throw new Error(result.error);
      toast.success("Invoice email sent", {
        description: `Receipt sent to your email address.`,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not resend invoice");
    } finally {
      setResendingId(null);
      setConfirmInvoice(null);
    }
  };

  const manageBilling = async () => {
    try {
      setPortalLoading(true);
      const result = await createPortalSession({
        data: {
          environment: getStripeEnvironment(),
          returnUrl: `${window.location.origin}/billing`,
        },
      });
      if ("error" in result) throw new Error(result.error);
      window.open(result.url, "_blank");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const totalSpent = (history ?? []).reduce(
    (sum, item) => sum + (item.amount_cents ?? 0),
    0
  );

  return (
    <div className="space-y-10">
      {/* Header */}
      <div>
        <h1 className="text-3xl text-gradient flex items-center gap-2">
          <CreditCard className="h-7 w-7 text-accent" />
          Billing & Payments
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your subscription, view receipts, and track spending.
        </p>
      </div>

      {/* Subscription Status Card */}
      <div className="glass rounded-3xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent" />
            Subscription
          </h2>
          {isActive ? (
            <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              Active
            </span>
          ) : (
            <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground border border-border">
              Not subscribed
            </span>
          )}
        </div>

        {subLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading subscription...
          </div>
        ) : isActive && subscription ? (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">Plan</div>
                <div className="text-sm font-medium capitalize">
                  {subscription.price_id?.replace("premium_", "Premium ") ?? "Premium"}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Status</div>
                <div className="text-sm font-medium capitalize">{subscription.status}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">
                  {subscription.cancel_at_period_end ? "Ends on" : "Renews on"}
                </div>
                <div className="text-sm font-medium">
                  {subscription.current_period_end
                    ? format(new Date(subscription.current_period_end), "MMM d, yyyy")
                    : "—"}
                </div>
              </div>
            </div>

            {subscription.cancel_at_period_end && (
              <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                Your subscription is set to cancel at the end of the current billing period.
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                onClick={manageBilling}
                disabled={portalLoading}
                variant="secondary"
                size="sm"
              >
                {portalLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Opening...
                  </>
                ) : (
                  <>
                    Manage billing
                    <ArrowUpRight className="h-4 w-4 ml-1.5" />
                  </>
                )}
              </Button>
              <Link to="/pricing">
                <Button variant="ghost" size="sm">
                  Change plan
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              You are not currently subscribed. Upgrade to unlock unlimited Sage AI
              counseling and premium features.
            </p>
            <Link to="/pricing">
              <Button size="sm" className="glow-primary">
                View plans
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Spending Summary */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="glass rounded-3xl p-6 space-y-1">
          <div className="text-xs text-muted-foreground">Total spent</div>
          <div className="text-3xl font-medium">
            ${(totalSpent / 100).toFixed(2)}
          </div>
          <div className="text-xs text-muted-foreground">
            Across all therapy sessions and subscriptions
          </div>
        </div>
        <div className="glass rounded-3xl p-6 space-y-1">
          <div className="text-xs text-muted-foreground">Sessions booked</div>
          <div className="text-3xl font-medium">{history?.length ?? 0}</div>
          <div className="text-xs text-muted-foreground">
            {history?.filter((h) => h.status === "paid").length ?? 0} paid
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="space-y-4">
        <h2 className="text-lg font-medium flex items-center gap-2">
          <Receipt className="h-5 w-5 text-accent" />
          Payment history
        </h2>

        {historyLoading ? (
          <div className="glass rounded-3xl p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Loading history...
          </div>
        ) : history && history.length > 0 ? (
          <div className="glass rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50 text-left text-muted-foreground">
                    <th className="px-6 py-3 font-medium">Description</th>
                    <th className="px-6 py-3 font-medium">Date</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-border/30 last:border-b-0 hover:bg-secondary/30 transition"
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium">{item.description}</div>
                        {item.scheduled_at && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <CalendarDays className="h-3 w-3" />
                            {format(new Date(item.scheduled_at), "MMM d, yyyy h:mm a")}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground whitespace-nowrap">
                        {format(new Date(item.created_at), "MMM d, yyyy")}
                      </td>
                      <td className="px-6 py-4">
                        <PaymentStatusBadge status={item.status} />
                      </td>
                      <td className="px-6 py-4 text-right font-medium whitespace-nowrap">
                        {item.amount_cents != null
                          ? `$${(item.amount_cents / 100).toFixed(2)}`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="glass rounded-3xl p-8 text-center text-sm text-muted-foreground">
            No payments yet. Book a therapy session or subscribe to a plan to see
            your history here.
          </div>
        )}
      </div>

      {/* Invoices */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium flex items-center gap-2">
            <FileText className="h-5 w-5 text-accent" />
            Invoices
          </h2>
          <span className="text-xs text-muted-foreground">
            Subscription & Stripe-generated receipts
          </span>
        </div>

        {invoicesLoading ? (
          <div className="glass rounded-3xl p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Loading invoices...
          </div>
        ) : invoicesData && invoicesData.length > 0 ? (
          <div className="glass rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50 text-left text-muted-foreground">
                    <th className="px-6 py-3 font-medium">Invoice</th>
                    <th className="px-6 py-3 font-medium">Date</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium text-right">Amount</th>
                    <th className="px-6 py-3 font-medium text-right">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {invoicesData.map((inv) => (
                    <tr
                      key={inv.id}
                      className="border-b border-border/30 last:border-b-0 hover:bg-secondary/30 transition"
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium">
                          {inv.number ?? inv.id.slice(0, 12)}
                        </div>
                        {inv.description && (
                          <div className="text-xs text-muted-foreground mt-0.5 max-w-xs truncate">
                            {inv.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground whitespace-nowrap">
                        {inv.created
                          ? format(new Date(inv.created), "MMM d, yyyy")
                          : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <PaymentStatusBadge status={inv.status ?? "—"} />
                      </td>
                      <td className="px-6 py-4 text-right font-medium whitespace-nowrap">
                        {inv.amount_paid.toLocaleString(undefined, {
                          style: "currency",
                          currency: inv.currency.toUpperCase(),
                        })}
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          {inv.pdf_url && (
                            <a
                              href={inv.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md hover:bg-secondary/60 text-foreground"
                              title="Download PDF"
                            >
                              <Download className="h-3.5 w-3.5" />
                              PDF
                            </a>
                          )}
                          {inv.hosted_invoice_url && (
                            <a
                              href={inv.hosted_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md hover:bg-secondary/60 text-muted-foreground"
                              title="View invoice"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => setConfirmInvoice({ id: inv.id, number: inv.number })}
                            disabled={resendingId === inv.id}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md hover:bg-secondary/60 text-muted-foreground disabled:opacity-50"
                            title="Resend invoice to your email"
                          >
                            {resendingId === inv.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Mail className="h-3.5 w-3.5" />
                            )}
                            Resend
                          </button>
                          {!inv.pdf_url && !inv.hosted_invoice_url && (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="glass rounded-3xl p-8 text-center text-sm text-muted-foreground">
            No invoices yet. Stripe issues invoices for subscription payments.
          </div>
        )}
      </div>

      {/* Resend Confirmation Dialog */}
      <AlertDialog open={!!confirmInvoice} onOpenChange={(open) => !open && setConfirmInvoice(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resend invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This will email the receipt for invoice{" "}
              <span className="font-medium text-foreground">
                {confirmInvoice?.number ?? confirmInvoice?.id?.slice(0, 12)}
              </span>{" "}
              to your registered email address.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmInvoice(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmInvoice && handleResend(confirmInvoice.id)}
              disabled={!!resendingId}
            >
              {resendingId === confirmInvoice?.id ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-1.5" />
              )}
              Resend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PaymentStatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  if (s === "paid" || s === "active") {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
        Paid
      </span>
    );
  }
  if (s === "unpaid") {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25">
        Unpaid
      </span>
    );
  }
  if (s === "refunded") {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/25">
        Refunded
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground border border-border capitalize">
      {status}
    </span>
  );
}
